import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as dictService from '../services/dictService';

const { TextArea } = Input;

const DictManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<dictService.DictType[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState('');
  const [searchCode, setSearchCode] = useState('');

  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<dictService.DictType | null>(null);
  const [typeForm] = Form.useForm();

  // 字典数据相关
  const [selectedType, setSelectedType] = useState<dictService.DictType | null>(null);
  const [dataList, setDataList] = useState<dictService.DictData[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataModalVisible, setDataModalVisible] = useState(false);
  const [editingData, setEditingData] = useState<dictService.DictData | null>(null);
  const [dataForm] = Form.useForm();

  // 加载字典类型
  const loadTypes = async () => {
    setLoading(true);
    try {
      const res = await dictService.listTypes({
        page,
        pageSize,
        name: searchName || undefined,
        code: searchCode || undefined,
      });
      setTypes(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载字典类型失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载字典数据
  const loadData = async (typeId: string) => {
    setDataLoading(true);
    try {
      const res = await dictService.listData({ typeId });
      setDataList(res.data || []);
    } catch {
      message.error('加载字典数据失败');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, [page, pageSize]);

  useEffect(() => {
    if (selectedType) {
      loadData(selectedType.id);
    }
  }, [selectedType]);

  // 搜索
  const handleSearch = () => {
    setPage(1);
    loadTypes();
  };

  // 重置
  const handleReset = () => {
    setSearchName('');
    setSearchCode('');
    setPage(1);
    loadTypes();
  };

  // ==================== 字典类型操作 ====================

  const handleAddType = () => {
    setEditingType(null);
    typeForm.resetFields();
    typeForm.setFieldsValue({ status: 1 });
    setTypeModalVisible(true);
  };

  const handleEditType = (record: dictService.DictType) => {
    setEditingType(record);
    typeForm.setFieldsValue(record);
    setTypeModalVisible(true);
  };

  const handleDeleteType = async (id: string) => {
    try {
      await dictService.deleteType(id);
      message.success('删除成功');
      loadTypes();
      if (selectedType?.id === id) {
        setSelectedType(null);
        setDataList([]);
      }
    } catch {
      message.error('删除失败');
    }
  };

  const handleTypeSubmit = async () => {
    try {
      const values = await typeForm.validateFields();
      if (editingType) {
        await dictService.updateType(editingType.id, values);
        message.success('更新成功');
      } else {
        await dictService.createType(values);
        message.success('创建成功');
      }
      setTypeModalVisible(false);
      loadTypes();
    } catch {
      // 表单验证失败
    }
  };

  // 选择字典类型查看数据
  const handleSelectType = (record: dictService.DictType) => {
    setSelectedType(record);
  };

  // ==================== 字典数据操作 ====================

  const handleAddData = () => {
    if (!selectedType) {
      message.warning('请先选择字典类型');
      return;
    }
    setEditingData(null);
    dataForm.resetFields();
    dataForm.setFieldsValue({ status: 1, sort: 0, isDefault: 0 });
    setDataModalVisible(true);
  };

  const handleEditData = (record: dictService.DictData) => {
    setEditingData(record);
    dataForm.setFieldsValue(record);
    setDataModalVisible(true);
  };

  const handleDeleteData = async (id: string) => {
    try {
      await dictService.deleteData(id);
      message.success('删除成功');
      if (selectedType) {
        loadData(selectedType.id);
      }
    } catch {
      message.error('删除失败');
    }
  };

  const handleDataSubmit = async () => {
    if (!selectedType) return;
    try {
      const values = await dataForm.validateFields();
      if (editingData) {
        await dictService.updateData(editingData.id, values);
        message.success('更新成功');
      } else {
        await dictService.createData({ ...values, typeId: selectedType.id });
        message.success('创建成功');
      }
      setDataModalVisible(false);
      loadData(selectedType.id);
    } catch {
      // 表单验证失败
    }
  };

  // 字典类型表格列
  const typeColumns: ColumnsType<dictService.DictType> = [
    { title: '字典名称', dataIndex: 'name', key: 'name', width: 120 },
    { title: '字典编码', dataIndex: 'code', key: 'code', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size={0} separator={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => handleSelectType(record)}>
            数据
          </Button>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => handleEditType(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除？会同时删除所有字典数据" onConfirm={() => handleDeleteType(record.id)}>
            <Button type="link" size="small" danger style={{ padding: '0 4px' }}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 字典数据表格列
  const dataColumns: ColumnsType<dictService.DictData> = [
    { title: '字典标签', dataIndex: 'label', key: 'label', width: 100 },
    { title: '字典值', dataIndex: 'value', key: 'value', width: 80 },
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 60 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '默认',
      dataIndex: 'isDefault',
      key: 'isDefault',
      width: 60,
      render: (isDefault) => (isDefault === 1 ? <Tag color="blue">是</Tag> : '-'),
    },
    {
      title: '样式',
      dataIndex: 'listClass',
      key: 'listClass',
      width: 80,
      render: (listClass, record) => (
        listClass ? <Tag color={listClass}>{record.label}</Tag> : '-'
      ),
    },
    { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space size={0} separator={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => handleEditData(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDeleteData(record.id)}>
            <Button type="link" size="small" danger style={{ padding: '0 4px' }}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16}>
        {/* 左侧：字典类型 */}
        <Col span={12}>
          <Card
            title="字典类型"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadTypes}>刷新</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddType}>
                  新增
                </Button>
              </Space>
            }
          >
            <Space style={{ marginBottom: 16 }}>
              <Input
                placeholder="字典名称"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                style={{ width: 120 }}
                allowClear
              />
              <Input
                placeholder="字典编码"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                style={{ width: 120 }}
                allowClear
              />
              <Button type="primary" onClick={handleSearch}>搜索</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>

            <Table
              columns={typeColumns}
              dataSource={types}
              rowKey="id"
              loading={loading}
              size="small"
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 条`,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              rowClassName={(record) =>
                selectedType?.id === record.id ? 'ant-table-row-selected' : ''
              }
              onRow={(record) => ({
                onClick: () => handleSelectType(record),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </Col>

        {/* 右侧：字典数据 */}
        <Col span={12}>
          <Card
            title={selectedType ? `字典数据 - ${selectedType.name}` : '字典数据'}
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => selectedType && loadData(selectedType.id)}
                  disabled={!selectedType}
                >
                  刷新
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddData}
                  disabled={!selectedType}
                >
                  新增
                </Button>
              </Space>
            }
          >
            {selectedType ? (
              <Table
                columns={dataColumns}
                dataSource={dataList}
                rowKey="id"
                loading={dataLoading}
                size="small"
                pagination={false}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 50, color: '#999' }}>
                请在左侧选择字典类型
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 字典类型弹窗 */}
      <Modal
        title={editingType ? '编辑字典类型' : '新增字典类型'}
        open={typeModalVisible}
        onCancel={() => setTypeModalVisible(false)}
        onOk={handleTypeSubmit}
        width={500}
      >
        <Form form={typeForm} layout="vertical">
          <Form.Item name="name" label="字典名称" rules={[{ required: true, message: '请输入字典名称' }]}>
            <Input placeholder="请输入字典名称" />
          </Form.Item>
          <Form.Item name="code" label="字典编码" rules={[{ required: true, message: '请输入字典编码' }]}>
            <Input placeholder="请输入字典编码（唯一标识）" disabled={!!editingType} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 字典数据弹窗 */}
      <Modal
        title={editingData ? '编辑字典数据' : '新增字典数据'}
        open={dataModalVisible}
        onCancel={() => setDataModalVisible(false)}
        onOk={handleDataSubmit}
        width={500}
      >
        <Form form={dataForm} layout="vertical">
          <Form.Item name="label" label="字典标签" rules={[{ required: true, message: '请输入字典标签' }]}>
            <Input placeholder="请输入字典标签（显示名称）" />
          </Form.Item>
          <Form.Item name="value" label="字典值" rules={[{ required: true, message: '请输入字典值' }]}>
            <Input placeholder="请输入字典值（实际值）" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="sort" label="排序">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态">
                <Select>
                  <Select.Option value={1}>启用</Select.Option>
                  <Select.Option value={0}>禁用</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isDefault" label="是否默认">
                <Select>
                  <Select.Option value={0}>否</Select.Option>
                  <Select.Option value={1}>是</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="listClass" label="列表样式">
            <Select allowClear placeholder="选择标签颜色">
              {dictService.listClassOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.value ? <Tag color={opt.value}>{opt.label}</Tag> : opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DictManagement;
